import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import BuscadorDesplegable from '../components/BuscadorDesplegable';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';

const fmt = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const itemVacio = { tipo:'producto', descripcion:'', producto_id:'', cantidad:1, precio_unitario:'', descuento_item:0 };

export default function Cotizaciones() {
  const hoy = new Date();
  const { usuario } = useAuth();
  const { empresa } = useEmpresa();
  const [vista, setVista]       = useState('lista');
  const [mes, setMes]           = useState(hoy.getMonth()+1);
  const [anio, setAnio]         = useState(hoy.getFullYear());
  const [cotizaciones, setCots] = useState([]);
  const [productos, setProd]    = useState([]);
  const [clientes, setClients]  = useState([]);
  const [buscar, setBuscar]     = useState('');
  const [cotActual, setCotActual]= useState(null);
  const pdfRef = useRef(null);
  const esAdmin = ['admin','superadmin'].includes(usuario?.rol);

  // Form
  const [cliente_id, setClienteId]        = useState('');
  const [cliente_nombre, setClienteNombre]= useState('');
  const [cliente_telefono, setClienteTel] = useState('');
  const [items, setItems]       = useState([{...itemVacio}]);
  const [descuento, setDesc]    = useState(0);
  const [fecha, setFecha]       = useState(hoy.toISOString().split('T')[0]);
  const [fecha_venc, setFechaVenc]= useState('');
  const [notas, setNotas]       = useState('');
  const [error, setError]       = useState('');
  const [guardando, setGuardando]= useState(false);
  const [modalConvertir, setModalConvertir]= useState(false);
  const [metodoPago, setMetodoPago]= useState('efectivo');

  const cargar = useCallback(async () => {
    const desde = `${anio}-${String(mes).padStart(2,'0')}-01`;
    const hasta = `${anio}-${String(mes).padStart(2,'0')}-${new Date(anio, mes, 0).getDate()}`;
    const params = new URLSearchParams({desde, hasta});
    if (buscar) params.append('buscar', buscar);
    const r = await api.get(`/api/cotizaciones?${params}`);
    setCots(r.data);
  }, [mes, anio, buscar]);

  useEffect(()=>{ cargar(); },[cargar]);
  useEffect(()=>{
    api.get('/api/catalogo?limit=2000&activo=true').then(r=>setProd(r.data.productos||[]));
    api.get('/api/clientes').then(r=>setClients(r.data));
  },[]);

  const subtotal = items.reduce((s,i)=>s+(parseFloat(i.precio_unitario||0)*parseInt(i.cantidad||1)-parseFloat(i.descuento_item||0)),0);
  const total = subtotal - parseFloat(descuento||0);

  const abrirNueva = () => {
    setClienteId(''); setClienteNombre(''); setClienteTel('');
    setItems([{...itemVacio}]); setDesc(0);
    setFecha(hoy.toISOString().split('T')[0]); setFechaVenc(''); setNotas('');
    setError(''); setVista('nueva');
  };

  const onClienteSelect = id => {
    setClienteId(id);
    const cl = clientes.find(c=>c.id===id);
    if(cl){ setClienteNombre(cl.nombre); setClienteTel(cl.telefono||''); }
  };

  const onProdSelect = (i, id) => {
    const p = productos.find(x=>x.id===id);
    setItems(prev=>prev.map((it,idx)=>idx===i
      ? {...it, producto_id:id, descripcion:p?p.nombre:it.descripcion, precio_unitario:p?p.precio_venta:it.precio_unitario, tipo:'producto'}
      : it
    ));
  };

  const addItem = () => setItems(p=>[...p,{...itemVacio}]);
  const removeItem = i => setItems(p=>p.filter((_,idx)=>idx!==i));
  const updItem = (i,k,v) => setItems(p=>p.map((it,idx)=>idx===i?{...it,[k]:v}:it));

  const guardar = async () => {
    const validos = items.filter(i=>i.descripcion&&i.precio_unitario);
    if(!validos.length){ setError('Agrega al menos un ítem con descripción y precio'); return; }
    setGuardando(true);
    try {
      const {data} = await api.post('/api/cotizaciones',{
        cliente_id:cliente_id||null, cliente_nombre:cliente_nombre||null,
        cliente_telefono:cliente_telefono||null, items:validos,
        descuento, fecha, fecha_vencimiento:fecha_venc||null, notas
      });
      const det = await api.get(`/api/cotizaciones/${data.id}`);
      setCotActual(det.data);
      cargar();
      setVista('detalle');
    } catch(e){ setError(e.response?.data?.error||'Error al guardar'); }
    finally{ setGuardando(false); }
  };

  const verDetalle = async id => {
    const {data} = await api.get(`/api/cotizaciones/${id}`);
    setCotActual(data); setVista('detalle');
  };

  const eliminar = async id => {
    if(!window.confirm('¿Eliminar esta cotización?')) return;
    await api.delete(`/api/cotizaciones/${id}`);
    cargar();
    if(vista==='detalle') setVista('lista');
  };

  const convertirEnVenta = async () => {
    try {
      const {data} = await api.post(`/api/cotizaciones/${cotActual.id}/convertir`, { metodo_pago: metodoPago });
      alert(`✅ Convertida en venta ${data.numero}`);
      setModalConvertir(false);
      const det = await api.get(`/api/cotizaciones/${cotActual.id}`);
      setCotActual(det.data);
      cargar();
    } catch(e){ alert(e.response?.data?.error||'Error al convertir'); }
  };

  const imprimirPDF = () => {
    if (!cotActual) return;
    const emp = empresa || {};
    const fmt2 = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);
    const filas = (cotActual.items||[]).map((it,idx) => `
      <tr style="background:${idx%2===0?'#f9fafb':'#fff'}">
        <td style="padding:9px 12px;border:1px solid #e5e7eb">${it.descripcion||'-'}</td>
        <td style="padding:9px 12px;border:1px solid #e5e7eb;text-align:center">${it.cantidad}</td>
        <td style="padding:9px 12px;border:1px solid #e5e7eb;text-align:right">${fmt2(it.precio_unitario)}</td>
        <td style="padding:9px 12px;border:1px solid #e5e7eb;text-align:right">${it.descuento_item>0?'-'+fmt2(it.descuento_item):'—'}</td>
        <td style="padding:9px 12px;border:1px solid #e5e7eb;text-align:right;font-weight:700">${fmt2(it.subtotal)}</td>
      </tr>`).join('');
    const logoHtml = emp.logo_base64
      ? `<img src="data:${emp.logo_mime||'image/png'};base64,${emp.logo_base64}" style="height:70px;object-fit:contain;" alt="Logo"/>`
      : `<div style="font-size:22px;font-weight:900;color:#1e3a5f">${emp.nombre||'Linkus Telecomunicaciones'}</div>`;
    const w = window.open('','_blank');
    w.document.write(`<html><head><title>Cotización ${cotActual.numero}</title><meta charset="UTF-8">
    <style>
      *{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:Arial,sans-serif;padding:30px 40px;color:#111;font-size:13px;background:#fff;}
      .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;padding-bottom:20px;border-bottom:3px solid #1e3a5f;}
      .empresa-info{font-size:12px;color:#555;margin-top:6px;line-height:1.7;}
      .doc-box{text-align:right;}
      .doc-titulo{font-size:26px;font-weight:900;color:#1e3a5f;letter-spacing:1px;}
      .doc-num{font-size:13px;color:#555;margin-top:4px;}
      .doc-estado{display:inline-block;background:${cotActual.estado==='enviada'?'#dbeafe':cotActual.estado==='aprobada'?'#dcfce7':'#fef9c3'};
        color:${cotActual.estado==='enviada'?'#1d4ed8':cotActual.estado==='aprobada'?'#15803d':'#a16207'};
        padding:4px 12px;border-radius:20px;font-size:11px;font-weight:700;text-transform:uppercase;margin-top:6px;}
      .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px;}
      .info-box{background:#f8fafc;border-radius:8px;padding:14px 18px;border:1px solid #e5e7eb;}
      .info-label{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;}
      .info-val{font-size:14px;font-weight:600;color:#1e3a5f;}
      .info-sub{font-size:12px;color:#6b7280;margin-top:2px;}
      table{width:100%;border-collapse:collapse;margin-bottom:20px;}
      th{background:#1e3a5f;color:#fff;padding:10px 12px;text-align:left;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;}
      .totales{display:flex;justify-content:flex-end;margin-bottom:24px;}
      .totales-inner{width:300px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;}
      .tot-row{display:flex;justify-content:space-between;padding:9px 16px;border-bottom:1px solid #e5e7eb;font-size:13px;}
      .tot-final{background:#1e3a5f;color:#fff;font-size:16px;font-weight:700;padding:12px 16px;display:flex;justify-content:space-between;}
      .notas-box{background:#f8fafc;border:1px solid #e5e7eb;border-radius:8px;padding:14px 18px;margin-bottom:24px;font-size:12px;color:#555;line-height:1.6;}
      .notas-label{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;}
      .pie{text-align:center;color:#9ca3af;font-size:11px;border-top:1px solid #e5e7eb;padding-top:16px;line-height:1.7;}
      @media print{body{padding:15px 20px;}}
    </style></head><body>
    <div class="header">
      <div>${logoHtml}
        <div class="empresa-info">
          ${emp.nit?`<strong>NIT:</strong> ${emp.nit}<br>`:''}
          ${emp.direccion?`${emp.direccion}<br>`:''}
          ${emp.telefono?`Tel: ${emp.telefono}${emp.telefono2?' / '+emp.telefono2:''}<br>`:''}
          ${emp.email?`${emp.email}`:''}
        </div>
      </div>
      <div class="doc-box">
        <div class="doc-titulo">COTIZACIÓN</div>
        <div class="doc-num">N° <strong>${cotActual.numero||'-'}</strong></div>
        <div class="doc-num">Fecha: ${cotActual.fecha ? new Date(cotActual.fecha).toLocaleDateString('es-CO') : '-'}</div>
        ${cotActual.fecha_vencimiento?`<div class="doc-num">Válida hasta: ${new Date(cotActual.fecha_vencimiento).toLocaleDateString('es-CO')}</div>`:''}
        <div class="doc-estado">${cotActual.estado||'borrador'}</div>
      </div>
    </div>
    <div class="info-grid">
      <div class="info-box">
        <div class="info-label">Cliente</div>
        <div class="info-val">${cotActual.cliente_nombre||cotActual.cliente_nombre_reg||'—'}</div>
        ${cotActual.cliente_telefono||cotActual.cliente_telefono_reg?`<div class="info-sub">Tel: ${cotActual.cliente_telefono||cotActual.cliente_telefono_reg}</div>`:''}
        ${cotActual.cliente_email||cotActual.cliente_email_reg?`<div class="info-sub">${cotActual.cliente_email||cotActual.cliente_email_reg}</div>`:''}
      </div>
      <div class="info-box">
        <div class="info-label">Resumen</div>
        <div class="info-val">${fmt2(cotActual.total)}</div>
        <div class="info-sub">${(cotActual.items||[]).length} ítem(s) · Validez ${cotActual.validez_dias||15} días</div>
      </div>
    </div>
    <table>
      <thead><tr>
        <th style="width:45%">Descripción</th>
        <th style="width:8%;text-align:center">Cant.</th>
        <th style="width:16%;text-align:right">Precio unit.</th>
        <th style="width:15%;text-align:right">Descuento</th>
        <th style="width:16%;text-align:right">Subtotal</th>
      </tr></thead>
      <tbody>${filas}</tbody>
    </table>
    <div class="totales"><div class="totales-inner">
      ${cotActual.subtotal>0?`<div class="tot-row"><span>Subtotal</span><span>${fmt2(cotActual.subtotal)}</span></div>`:''}
      ${cotActual.descuento>0?`<div class="tot-row"><span>Descuento general</span><span>-${fmt2(cotActual.descuento)}</span></div>`:''}
      <div class="tot-final"><span>TOTAL</span><span>${fmt2(cotActual.total)}</span></div>
    </div></div>
    ${cotActual.notas?`<div class="notas-box"><div class="notas-label">Notas y condiciones</div>${cotActual.notas}</div>`:''}
    <div class="pie">
      ${emp.pie_factura||'Gracias por confiar en nosotros'}<br>
      ${emp.nombre||'Linkus Telecomunicaciones'}${emp.telefono?' · Tel: '+emp.telefono:''} ${emp.email?' · '+emp.email:''}
    </div>
    </body></html>`);
    w.document.close();
    setTimeout(()=>w.print(),300);
  };

  const s = {
    page:    { padding:'24px', maxWidth:'1280px', margin:'0 auto' },
    topBar:  { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px', flexWrap:'wrap', gap:'12px', position:'sticky', top:0, zIndex:100, background:'#0A0F1E', padding:'12px 0', margin:'0 0 24px' },
    h1:      { fontSize:'22px', fontWeight:'700', margin:0, color:'#fff' },
    sub:     { fontSize:'13px', color:'rgba(255,255,255,0.4)', margin:'4px 0 0' },
    card:    { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'14px', padding:'20px', marginBottom:'16px' },
    cardTitle:{ fontSize:'14px', fontWeight:'700', color:'rgba(255,255,255,0.7)', marginBottom:'14px', marginTop:0 },
    btnPrimary:{ background:'linear-gradient(135deg,#00D4FF,#4F46E5)', border:'none', borderRadius:'9px', padding:'10px 20px', color:'#fff', fontSize:'14px', fontWeight:'700', cursor:'pointer' },
    btnBack: { background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'8px 14px', color:'rgba(255,255,255,0.7)', fontSize:'13px', cursor:'pointer' },
    btnG:    { background:'rgba(34,197,94,0.15)', border:'1px solid rgba(34,197,94,0.3)', borderRadius:'8px', padding:'8px 16px', color:'#86EFAC', fontSize:'13px', cursor:'pointer', fontWeight:'600' },
    btnW:    { background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'8px 14px', color:'rgba(255,255,255,0.7)', fontSize:'13px', cursor:'pointer' },
    btnR:    { background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'8px 14px', color:'#FCA5A5', fontSize:'13px', cursor:'pointer' },
    btnAdd:  { background:'rgba(0,212,255,0.1)', border:'1px solid rgba(0,212,255,0.25)', borderRadius:'7px', padding:'6px 14px', color:'#00D4FF', fontSize:'13px', cursor:'pointer', fontWeight:'600' },
    btnRemove:{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'6px', padding:'5px 10px', color:'#FCA5A5', fontSize:'12px', cursor:'pointer' },
    input:   { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'9px 12px', color:'rgba(255,255,255,0.9)', fontSize:'13px', outline:'none', width:'100%', boxSizing:'border-box', fontFamily:'inherit' },
    label:   { fontSize:'11px', fontWeight:'700', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.8px', display:'block', marginBottom:'4px' },
    th:      { textAlign:'left', padding:'10px 14px', fontSize:'11px', fontWeight:'700', color:'rgba(255,255,255,0.4)', borderBottom:'1px solid rgba(255,255,255,0.08)', textTransform:'uppercase' },
    td:      { padding:'12px 14px', fontSize:'13px', borderBottom:'1px solid rgba(255,255,255,0.05)', color:'rgba(255,255,255,0.8)', verticalAlign:'middle' },
    itemCard:{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'14px', marginBottom:'10px', overflow:'visible', position:'relative', zIndex:'auto' },
    errorBox:{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'10px', color:'#FCA5A5', fontSize:'13px' },
    periodo: { display:'flex', alignItems:'center', gap:'12px', marginBottom:'20px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'12px 16px', flexWrap:'wrap' },
    mesBtn:  { background:'none', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'6px', padding:'4px 10px', color:'rgba(255,255,255,0.5)', fontSize:'12px', cursor:'pointer' },
    mesBtnOn:{ background:'rgba(0,212,255,0.15)', border:'1px solid rgba(0,212,255,0.4)', color:'#00D4FF', fontWeight:'700' },
    anioSel: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'6px 12px', color:'#fff', fontSize:'13px', outline:'none' },
  };

  // ── VISTA LISTA ──────────────────────────────────────────────
  if(vista==='lista') return (
    <Layout>
      <div className="pagina" style={s.page}>
        <div className="top-sticky" style={s.topBar}>
          <div><h1 style={s.h1}>📋 Cotizaciones</h1><p style={s.sub}>Historial de cotizaciones generadas</p></div>
          {esAdmin && <button style={s.btnPrimary} onClick={abrirNueva}>+ Nueva Cotización</button>}
        </div>

        {/* Período */}
        <div style={s.periodo}>
          <span style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', whiteSpace:'nowrap' }}>Período:</span>
          <div className="periodo-meses" style={{display:'flex',gap:'4px',flex:1,flexWrap:'wrap'}}>
            {MESES.map((m,i)=>(
              <button key={i} onClick={()=>setMes(i+1)} style={{...s.mesBtn,...(mes===i+1?s.mesBtnOn:{})}}>
                {m.slice(0,3)}
              </button>
            ))}
          </div>
          <select style={s.anioSel} value={anio} onChange={e=>setAnio(+e.target.value)}>
            {[anio-1,anio,anio+1].map(a=><option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        {/* Buscador */}
        <input style={{...s.input,marginBottom:'16px'}} placeholder="🔍 Buscar por cliente o N° cotización..." value={buscar} onChange={e=>setBuscar(e.target.value)}/>

        {/* En el celular, una tarjeta por cotización y se toca entera para verla. La tabla
            escondía el total (responsive.css oculta las columnas 4 y 5) y dejaba «Ver» fuera
            de la pantalla, a la derecha. */}
        {cotizaciones.length>0 && (
          <div className="solo-movil" style={{display:'flex',flexDirection:'column',gap:'10px'}}>
            {cotizaciones.map(c=>(
              <div key={c.id} style={{...s.card,marginBottom:0,padding:'14px 16px',cursor:'pointer'}} onClick={()=>verDetalle(c.id)}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'8px'}}>
                  <code style={{color:'#00D4FF',fontFamily:'monospace',fontWeight:'700',fontSize:'14px'}}>{c.numero}</code>
                  {c.convertida_en_venta
                    ? <span style={{background:'rgba(34,197,94,0.15)',color:'#86EFAC',padding:'3px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap'}}>✓ Convertida</span>
                    : <span style={{background:'rgba(245,158,11,0.15)',color:'#FCD34D',padding:'3px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap'}}>Pendiente</span>
                  }
                </div>
                <div style={{fontSize:'15px',fontWeight:'600',color:'#fff',margin:'8px 0 6px',overflowWrap:'anywhere'}}>{c.cliente_nombre||c.cliente_nombre_reg||'—'}</div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',gap:'8px'}}>
                  <span style={{fontSize:'12px',color:'rgba(255,255,255,0.45)'}}>
                    {new Date(c.fecha).toLocaleDateString('es-CO')}
                    {c.fecha_vencimiento && <> · vence {new Date(c.fecha_vencimiento).toLocaleDateString('es-CO')}</>}
                  </span>
                  <strong style={{color:'#22C55E',fontSize:'16px',whiteSpace:'nowrap'}}>{fmt(c.total)}</strong>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabla */}
        <div className={cotizaciones.length>0 ? 'solo-escritorio' : undefined} style={{...s.card,padding:0,overflow:'hidden'}}>
          {cotizaciones.length===0 ? (
            <div style={{padding:'60px 20px',textAlign:'center',color:'rgba(255,255,255,0.3)'}}>
              <div style={{fontSize:'40px',marginBottom:'10px'}}>📋</div>
              <p>No hay cotizaciones para este período</p>
            </div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr style={{background:'rgba(255,255,255,0.03)'}}>
                <th style={s.th}>N° Cotización</th>
                <th style={s.th}>Fecha</th>
                <th style={s.th}>Cliente</th>
                <th style={s.th}>Total</th>
                <th style={s.th}>Vencimiento</th>
                <th style={s.th}>Estado</th>
                <th style={s.th}>Acciones</th>
              </tr></thead>
              <tbody>
                {cotizaciones.map(c=>(
                  <tr key={c.id}>
                    <td style={s.td}><code style={{color:'#00D4FF',fontFamily:'monospace',fontWeight:'700'}}>{c.numero}</code></td>
                    <td style={s.td}><span style={{fontSize:'12px',color:'rgba(255,255,255,0.5)'}}>{new Date(c.fecha).toLocaleDateString('es-CO')}</span></td>
                    <td style={s.td}>{c.cliente_nombre||c.cliente_nombre_reg||'—'}</td>
                    <td style={s.td}><strong style={{color:'#22C55E'}}>{fmt(c.total)}</strong></td>
                    <td style={s.td}>{c.fecha_vencimiento?<span style={{fontSize:'12px',color:'rgba(255,255,255,0.5)'}}>{new Date(c.fecha_vencimiento).toLocaleDateString('es-CO')}</span>:'—'}</td>
                    <td style={s.td}>
                      {c.convertida_en_venta
                        ? <span style={{background:'rgba(34,197,94,0.15)',color:'#86EFAC',padding:'3px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:'700'}}>✓ Convertida</span>
                        : <span style={{background:'rgba(245,158,11,0.15)',color:'#FCD34D',padding:'3px 10px',borderRadius:'20px',fontSize:'11px',fontWeight:'700'}}>Pendiente</span>
                      }
                    </td>
                    <td style={s.td}>
                      <div style={{display:'flex',gap:'6px'}}>
                        <button style={{...s.btnW,padding:'5px 12px',border:'none',borderRadius:'6px',cursor:'pointer',fontSize:'12px'}} onClick={()=>verDetalle(c.id)}>Ver</button>
                        {esAdmin && !c.convertida_en_venta && <button style={{...s.btnR,padding:'5px 10px',fontSize:'12px'}} onClick={()=>eliminar(c.id)}>✕</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );

  // ── VISTA NUEVA COTIZACIÓN ───────────────────────────────────
  if(vista==='nueva') return (
    <Layout>
      <div className="pagina" style={s.page}>
        <div className="top-sticky" style={s.topBar}>
          <div style={{display:'flex',alignItems:'center',gap:'12px'}}>
            <button style={s.btnBack} onClick={()=>setVista('lista')}>← Volver</button>
            <div><h1 style={s.h1}>Nueva Cotización</h1><p style={s.sub}>Completa los datos</p></div>
          </div>
          <button style={s.btnPrimary} onClick={guardar} disabled={guardando}>{guardando?'Guardando...':'✓ Guardar cotización'}</button>
        </div>

        <div className="layout-2col" style={{display:'grid',gridTemplateColumns:'minmax(0,1fr) 300px',gap:'20px',alignItems:'start'}}>
          <div>
            {/* Cliente */}
            <div style={s.card}>
              <h3 style={s.cardTitle}>👤 Cliente</h3>
              <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                <div>
                  <label style={s.label}>Cliente registrado</label>
                  <BuscadorDesplegable opciones={clientes} valor={cliente_id} onChange={onClienteSelect}
                    placeholder="— Seleccionar cliente —" formatear={c=>`${c.nombre}${c.cedula?' — '+c.cedula:''}`}/>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <div><label style={s.label}>Nombre</label><input style={s.input} value={cliente_nombre} onChange={e=>setClienteNombre(e.target.value)} placeholder="Nombre del cliente"/></div>
                  <div><label style={s.label}>Teléfono</label><input style={s.input} value={cliente_telefono} onChange={e=>setClienteTel(e.target.value)} placeholder="Teléfono"/></div>
                </div>
              </div>
            </div>

            {/* Ítems */}
            <div style={s.card}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'14px'}}>
                <h3 style={{...s.cardTitle,margin:0}}>🛒 Productos / Servicios</h3>
                <button style={s.btnAdd} onClick={addItem}>+ Agregar ítem</button>
              </div>
              {items.map((item,i)=>(
                <div key={i} style={s.itemCard}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
                    <select style={{...s.input,width:'auto'}} value={item.tipo} onChange={e=>updItem(i,'tipo',e.target.value)}>
                      <option value="producto">📦 Producto</option>
                      <option value="servicio">🔧 Servicio</option>
                    </select>
                    {items.length>1 && <button style={s.btnRemove} onClick={()=>removeItem(i)}>✕ Eliminar</button>}
                  </div>
                  {item.tipo==='producto' && (
                    <div style={{marginBottom:'8px'}}>
                      <label style={s.label}>Seleccionar del catálogo</label>
                      <BuscadorDesplegable opciones={productos} valor={item.producto_id} onChange={id=>onProdSelect(i,id)}
                        placeholder="— Buscar producto —" formatear={p=>`[${p.sku}] ${p.nombre} — ${fmt(p.precio_venta)}`}/>
                    </div>
                  )}
                  {/* La descripción va sola en su renglón: en media columna, en el celular se leían
                      nueve letras. Los tres números se reparten lo que quepa (tres en el computador,
                      dos y uno en el celular). */}
                  <div style={{marginBottom:'8px'}}><label style={s.label}>Descripción</label><input style={s.input} value={item.descripcion} onChange={e=>updItem(i,'descripcion',e.target.value)} placeholder="Descripción del producto o servicio"/></div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))',gap:'8px',marginBottom:'0'}}>
                    <div><label style={s.label}>Cantidad</label><input style={s.input} type="number" min="1" value={item.cantidad} onChange={e=>updItem(i,'cantidad',e.target.value)}/></div>
                    <div><label style={s.label}>Precio unit.</label><input style={s.input} type="number" value={item.precio_unitario} onChange={e=>updItem(i,'precio_unitario',e.target.value)} placeholder="0"/></div>
                    <div><label style={s.label}>Descuento</label><input style={s.input} type="number" min="0" value={item.descuento_item} onChange={e=>updItem(i,'descuento_item',e.target.value)} placeholder="0"/></div>
                  </div>
                  <div style={{textAlign:'right',marginTop:'8px',fontSize:'13px',fontWeight:'700',color:'#22C55E'}}>
                    {fmt(parseFloat(item.precio_unitario||0)*parseInt(item.cantidad||1)-parseFloat(item.descuento_item||0))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Columna derecha */}
          <div style={{display:'flex',flexDirection:'column',gap:'16px'}}>
            <div style={s.card}>
              <h3 style={s.cardTitle}>💰 Resumen</h3>
              {items.map((it,i)=>(
                <div key={i} style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'rgba(255,255,255,0.5)',marginBottom:'4px'}}>
                  <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginRight:'8px'}}>{it.descripcion||`Ítem ${i+1}`}</span>
                  <span>{fmt(parseFloat(it.precio_unitario||0)*parseInt(it.cantidad||1)-parseFloat(it.descuento_item||0))}</span>
                </div>
              ))}
              <div style={{borderTop:'1px solid rgba(255,255,255,0.08)',paddingTop:'8px',marginTop:'8px'}}>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'rgba(255,255,255,0.5)',marginBottom:'4px'}}><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                {parseFloat(descuento)>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'#FCA5A5',marginBottom:'4px'}}><span>Descuento</span><span>-{fmt(descuento)}</span></div>}
                <div style={{display:'flex',justifyContent:'space-between',fontSize:'20px',fontWeight:'800',color:'#22C55E',marginTop:'8px'}}><span>TOTAL</span><span>{fmt(total)}</span></div>
              </div>
            </div>

            <div style={s.card}>
              <h3 style={s.cardTitle}>📅 Datos</h3>
              <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
                <div><label style={s.label}>Fecha</label><input style={s.input} type="date" value={fecha} onChange={e=>setFecha(e.target.value)}/></div>
                <div><label style={s.label}>Válida hasta (opcional)</label><input style={s.input} type="date" value={fecha_venc} onChange={e=>setFechaVenc(e.target.value)}/></div>
                <div><label style={s.label}>Descuento general</label><input style={s.input} type="number" min="0" value={descuento} onChange={e=>setDesc(e.target.value)} placeholder="0"/></div>
                <div><label style={s.label}>Notas</label><textarea style={{...s.input,minHeight:'70px',resize:'vertical'}} value={notas} onChange={e=>setNotas(e.target.value)} placeholder="Condiciones, observaciones..."/></div>
              </div>
            </div>

            {error && <div style={s.errorBox}>{error}</div>}
            <button style={{...s.btnPrimary,width:'100%',padding:'14px',fontSize:'15px'}} onClick={guardar} disabled={guardando}>
              {guardando?'Guardando...':'✓ Guardar y ver cotización'}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );

  // ── VISTA DETALLE / PDF ──────────────────────────────────────
  return (
    <Layout>
      <div className="pagina" style={s.page}>
        <div className="top-sticky" style={s.topBar}>
          <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
            <button style={s.btnBack} onClick={()=>setVista('lista')}>← Volver</button>
            <h1 style={s.h1}>{cotActual?.numero}</h1>
            {cotActual?.convertida_en_venta && <span style={{background:'rgba(34,197,94,0.15)',color:'#86EFAC',padding:'4px 12px',borderRadius:'20px',fontSize:'12px',fontWeight:'700'}}>✓ Convertida en venta</span>}
          </div>
          <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
            <button style={s.btnW} onClick={imprimirPDF}>🖨️ Imprimir PDF</button>
            {esAdmin && !cotActual?.convertida_en_venta && (
              <button style={s.btnG} onClick={()=>setModalConvertir(true)}>💰 Convertir en venta</button>
            )}
            {esAdmin && !cotActual?.convertida_en_venta && (
              <button style={s.btnR} onClick={()=>eliminar(cotActual?.id)}>🗑️ Eliminar</button>
            )}
            {esAdmin && <button style={s.btnPrimary} onClick={abrirNueva}>+ Nueva Cotización</button>}
          </div>
        </div>

        {/* Vista PDF imprimible */}
        <div style={{maxWidth:'700px',margin:'0 auto'}}>
          <div ref={pdfRef} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'14px',padding:'clamp(16px,5vw,28px)'}}>
            {/* Encabezado */}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:'12px',marginBottom:'20px',paddingBottom:'16px',borderBottom:'1px solid rgba(255,255,255,0.1)'}}>
              <div>
                <h2 style={{fontSize:'20px',fontWeight:'800',margin:'0 0 4px',color:'#fff'}}>{empresa?.nombre||'LINKUS TELECOMUNICACIONES'}</h2>
                {empresa?.nit&&<p style={{fontSize:'12px',color:'rgba(255,255,255,0.5)',margin:'2px 0'}}>NIT: {empresa.nit}</p>}
                {empresa?.telefono&&<p style={{fontSize:'12px',color:'rgba(255,255,255,0.5)',margin:'2px 0'}}>Tel: {empresa.telefono}</p>}
                {empresa?.direccion&&<p style={{fontSize:'12px',color:'rgba(255,255,255,0.5)',margin:'2px 0'}}>{empresa.direccion}</p>}
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:'22px',fontWeight:'800',color:'#00D4FF'}}>{cotActual?.numero}</div>
                <div style={{fontSize:'12px',color:'rgba(255,255,255,0.5)',marginTop:'4px'}}>COTIZACIÓN</div>
                <div style={{fontSize:'12px',color:'rgba(255,255,255,0.5)'}}>Fecha: {cotActual?.fecha&&new Date(cotActual.fecha).toLocaleDateString('es-CO')}</div>
                {cotActual?.fecha_vencimiento&&<div style={{fontSize:'12px',color:'#FCD34D'}}>Válida hasta: {new Date(cotActual.fecha_vencimiento).toLocaleDateString('es-CO')}</div>}
              </div>
            </div>

            {/* Cliente */}
            <div style={{marginBottom:'20px'}}>
              <p style={{fontSize:'11px',fontWeight:'700',color:'rgba(255,255,255,0.4)',textTransform:'uppercase',letterSpacing:'1px',marginBottom:'8px'}}>Cliente</p>
              <p style={{fontSize:'15px',fontWeight:'700',color:'#fff',margin:'0 0 4px'}}>{cotActual?.cliente_nombre||cotActual?.cliente_nombre_reg||'Consumidor final'}</p>
              {(cotActual?.cliente_telefono||cotActual?.cliente_telefono_reg)&&<p style={{fontSize:'13px',color:'rgba(255,255,255,0.5)',margin:0}}>📞 {cotActual?.cliente_telefono||cotActual?.cliente_telefono_reg}</p>}
            </div>

            {/* Ítems. En el celular, renglón por producto: la tabla perdía la columna del total
                (responsive.css oculta la cuarta en todas las tablas). */}
            <div className="solo-movil" style={{marginBottom:'20px',borderTop:'1px solid rgba(255,255,255,0.08)'}}>
              {cotActual?.items?.map((it,i)=>(
                <div key={i} style={{padding:'10px 0',borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                  <div style={{fontSize:'14px',color:'rgba(255,255,255,0.85)',overflowWrap:'anywhere'}}>{it.descripcion}</div>
                  <div style={{display:'flex',justifyContent:'space-between',gap:'8px',marginTop:'4px',fontSize:'13px'}}>
                    <span style={{color:'rgba(255,255,255,0.5)'}}>{it.cantidad} × {fmt(it.precio_unitario)}</span>
                    <strong style={{color:'#fff',whiteSpace:'nowrap'}}>{fmt(it.subtotal)}</strong>
                  </div>
                </div>
              ))}
            </div>
            <table className="solo-escritorio" style={{width:'100%',borderCollapse:'collapse',marginBottom:'20px'}}>
              <thead>
                <tr style={{background:'rgba(255,255,255,0.04)'}}>
                  <th style={{...s.th,padding:'10px 12px'}}>Descripción</th>
                  <th style={{...s.th,padding:'10px 12px',textAlign:'center'}}>Cant.</th>
                  <th style={{...s.th,padding:'10px 12px',textAlign:'right'}}>Precio unit.</th>
                  <th style={{...s.th,padding:'10px 12px',textAlign:'right'}}>Total</th>
                </tr>
              </thead>
              <tbody>
                {cotActual?.items?.map((it,i)=>(
                  <tr key={i}>
                    <td style={{...s.td,padding:'10px 12px'}}>{it.descripcion}</td>
                    <td style={{...s.td,padding:'10px 12px',textAlign:'center'}}>{it.cantidad}</td>
                    <td style={{...s.td,padding:'10px 12px',textAlign:'right'}}>{fmt(it.precio_unitario)}</td>
                    <td style={{...s.td,padding:'10px 12px',textAlign:'right',fontWeight:'600'}}>{fmt(it.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totales */}
            <div style={{maxWidth:'260px',marginLeft:'auto'}}>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'rgba(255,255,255,0.6)',marginBottom:'6px'}}><span>Subtotal:</span><span>{fmt(cotActual?.subtotal)}</span></div>
              {parseFloat(cotActual?.descuento)>0&&<div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',color:'#FCA5A5',marginBottom:'6px'}}><span>Descuento:</span><span>-{fmt(cotActual?.descuento)}</span></div>}
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'20px',fontWeight:'800',color:'#22C55E',borderTop:'1px solid rgba(255,255,255,0.1)',paddingTop:'8px',marginTop:'4px'}}><span>TOTAL:</span><span>{fmt(cotActual?.total)}</span></div>
            </div>

            {cotActual?.notas&&<div style={{marginTop:'20px',padding:'12px',background:'rgba(255,255,255,0.03)',borderRadius:'8px',fontSize:'13px',color:'rgba(255,255,255,0.5)'}}>
              <strong style={{color:'rgba(255,255,255,0.7)'}}>Notas: </strong>{cotActual.notas}
            </div>}

            <p style={{textAlign:'center',color:'rgba(255,255,255,0.3)',fontSize:'11px',marginTop:'20px'}}>{empresa?.pie_factura||'¡Gracias por su preferencia!'}</p>
          </div>
        </div>

        {/* Modal convertir */}
        {modalConvertir && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{background:'#0F1629',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'14px',padding:'28px',width:'100%',maxWidth:'380px'}}>
              <h2 style={{fontSize:'16px',fontWeight:'700',marginBottom:'16px',color:'#fff'}}>💰 Convertir en venta</h2>
              <p style={{fontSize:'14px',color:'rgba(255,255,255,0.6)',marginBottom:'16px'}}>Total: <strong style={{color:'#22C55E',fontSize:'18px'}}>{fmt(cotActual?.total)}</strong></p>
              <div style={{marginBottom:'16px'}}>
                <label style={s.label}>Método de pago</label>
                <select style={s.input} value={metodoPago} onChange={e=>setMetodoPago(e.target.value)}>
                  {['efectivo','transferencia','nequi','daviplata','pse','otro'].map(m=><option key={m} value={m}>{m.charAt(0).toUpperCase()+m.slice(1)}</option>)}
                </select>
              </div>
              <div style={{display:'flex',gap:'10px',justifyContent:'flex-end'}}>
                <button style={s.btnW} onClick={()=>setModalConvertir(false)}>Cancelar</button>
                <button style={s.btnPrimary} onClick={convertirEnVenta}>✓ Confirmar venta</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
